import { lt } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ContactListItem from '../contact-list-item.gjs';
import ContactSearch from '../contact-search.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import styles from './contacts.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesContacts extends Component {
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get routeHandler() {
    return this.args.handler;
  }

  get isGroup() {
    return this.routeHandler.isGroup;
  }

  get search() {
    return this.routeHandler.search;
  }

  get isRecentContacts() {
    return this.routeHandler.isRecentContacts;
  }

  get isFiltered() {
    return this.routeHandler.shouldFilter;
  }

  get selectedContacts() {
    return this.routeHandler.selectedContacts;
  }

  get contactList() {
    if (!this.routeHandler.selectableContacts || this.routeHandler.getContacts.isRunning) {
      return [];
    }

    const contacts = this.routeHandler.selectableContacts;

    if (this.isFiltered) {
      return contacts;
    }

    // If no search, show recent clients as default
    return contacts.filter(contact => contact.isRecent);
  }

  leaveContactPage = () => {
    this.messagingNavigation.toConversationList();
  };

  updateSearch = event => {
    this.routeHandler.updateSearch(event.target.value);
  };

  clearSearch = () => {
    this.routeHandler.updateSearch('');
  };

  loadMoreContacts = model => {
    this.routeHandler.loadMoreContacts.perform(model);
  };

  isContactSelected = contact => this.routeHandler.isContactSelected(contact.id);

  <template>
    <BaseRoute class="{{styles.component}} contacts-page" data-test-contacts-route>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.leaveContactPage}}
          class="back-button"
          data-test-contacts-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>
        <span data-test-contacts-page-title>
          {{#if this.isGroup}}
            {{t "emc.contacts_page.new_group_message"}}
          {{else}}
            {{t "emc.contacts_page.new_message"}}
          {{/if}}
        </span>
      </:header>
      <:default>
        <ContactSearch
          @clearSearch={{this.clearSearch}}
          @isGroup={{this.isGroup}}
          @search={{this.search}}
          @updateSearch={{this.updateSearch}}
        />
        {{#if this.routeHandler.getContacts.isRunning}}
          <div
            class="spinner"
            data-test-loading-spinner
            aria-live="polite"
            aria-label={{t "emc.contacts_page.loading"}}
          >
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.contactList}}
          {{#if this.isRecentContacts}}
            <div class="recent-conversations-label" data-test-recent-contacts-label>
              {{t "emc.contacts_page.recent_conversations"}}
            </div>
          {{/if}}
          <ul
            class="contact-list-body"
            data-test-contact-list-body
            role="listbox"
            aria-label={{t "emc.contacts_page.list"}}
          >
            {{#each this.contactList as |contact|}}
              <ContactListItem
                @contact={{contact}}
                @isSelected={{this.isContactSelected contact}}
                @onSelect={{this.routeHandler.selectContact}}
                @showCheckbox={{this.isGroup}}
              />
            {{/each}}
            {{#if this.isFiltered}}
              <SharedInfinityLoader
                @model={{this.routeHandler.contacts}}
                @infinityLoad={{this.loadMoreContacts}}
                aria-label={{t "emc.contacts_page.infinity_loader_label"}}
              />
            {{/if}}
          </ul>
        {{else}}
          <div
            class="empty-contact-list"
            data-test-empty-contact-list
            role="status"
            aria-live="polite"
            aria-label={{t "emc.contacts_page.no_contacts_found"}}
          >
          </div>
        {{/if}}
      </:default>
      <:footer>
        {{#if this.isGroup}}
          <PrimaryButton
            @disabled={{lt this.selectedContacts.length 2}}
            data-test-create-group-button
          >
            {{t "emc.contacts_page.create_group"}}
            {{#if this.selectedContacts.length}}
              ({{this.selectedContacts.length}})
            {{/if}}
          </PrimaryButton>
        {{/if}}
      </:footer>
    </BaseRoute>
  </template>
}
